import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, FileImage, ExternalLink } from "lucide-react";
import api, { API } from "../lib/api";

export default function COASection() {
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/coas").then((r) => setCoas(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <section id="coa" className="relative py-24 md:py-32 border-t border-white/10 bg-[#050507]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-[11px] tracking-[0.4em] uppercase text-[#00F0FF] mb-4 font-mono2"
        >
          // Certificates of Analysis
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="font-display font-black text-3xl md:text-5xl uppercase tracking-tighter mb-4"
        >
          Lab-Verified <span className="text-[#00F0FF] neon-text">COAs</span>
        </motion.h2>
        <p className="text-xs text-slate-500 font-mono2 max-w-xl mb-14">
          Third-party purity &amp; identity reports. New certificates are published here as each batch is tested.
        </p>

        {loading ? (
          <p className="text-slate-500 font-mono2 text-sm">Loading…</p>
        ) : coas.length === 0 ? (
          <div className="border border-dashed border-white/15 p-12 text-center" data-testid="coa-empty">
            <FileText size={32} className="text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-mono2 text-sm">Certificates of Analysis will appear here as they become available.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coa-grid">
            {coas.map((c, i) => {
              const isPdf = (c.content_type || "").includes("pdf");
              return (
                <motion.a
                  key={c.id}
                  href={`${API}/coas/${c.id}/download`} target="_blank" rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                  className="group flex items-center gap-4 border border-white/10 hover:border-[#00F0FF]/50 bg-white/[0.02] p-4 transition-colors"
                  data-testid={`coa-${c.id}`}
                >
                  <span className="flex items-center justify-center w-11 h-11 border border-[#00F0FF]/40 text-[#00F0FF] group-hover:bg-[#00F0FF] group-hover:text-black transition-colors shrink-0">
                    {isPdf ? <FileText size={18} /> : <FileImage size={18} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono2 text-sm truncate">{c.title}</p>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2 truncate">
                      {c.product_name || "General"} · View <ExternalLink size={10} className="inline -mt-0.5" />
                    </p>
                  </div>
                </motion.a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
